import Button from 'components/Button';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Row } from 'components/Layout';
import Spacing from 'components/Spacing';
import { CircularProgress, Card, CardContent, Typography } from '@mui/material';
import PageSection from 'components/PageSection';
import Heading from 'components/Heading';
import { useState } from 'react';
import FeesAccordion from './FeesAccordion';
import { ListLpFeesCategory } from 'further-types/lp/lp-fee';
import { numberToCurrencyString } from 'further-ui/utils';
import ButtonGroup from 'components/ButtonGroup';
import { styled } from '@mui/material/styles';
import useFeeManager from 'hooks/lp/data/fee-manager/useFeeManager';
import { ApprovalSummaryData } from '..';

const StyledRow = styled(Row)(() => ({
  gap: 40,
}));

const StyledCard = styled(Card)(() => ({
  flex: 1,
  minHeight: 158,
}));

const StyledCardContent = styled(CardContent)(() => ({
  alignItems: 'center',
  justifyContent: 'center',
}));

const FeeManager: React.FC<{
  setApprovalSummaryData: (data: ApprovalSummaryData) => void;
}> = ({ setApprovalSummaryData }) => {
  const { selectedVehicleId } = useLayoutContext();
  const [showApprovedFees, setShowApprovedFees] = useState(false);

  const {
    dueUnapprovedFees,
    futureUnapprovedFees,
    approvedFees,
    isLoadingDueFees,
    isLoadingFutureFees,
    isLoadingApprovedFees,
    dueFeesPage,
    futureFeesPage,
    approvedFeesPage,
    setDueFeesPage,
    setFutureFeesPage,
    setApprovedFeesPage,
    exportFeesMutation,
  } = useFeeManager();

  const handleExport = async () => {
    if (!selectedVehicleId) return;

    exportFeesMutation.mutate({
      vehicleId: selectedVehicleId,
    });
  };

  const calculateTotalApprovedFees = approvedFees?.feesTotalValue ?? 0;

  const calculateTotalUnapprovedFees =
    (dueUnapprovedFees?.feesTotalValue ?? 0) +
    (futureUnapprovedFees?.feesTotalValue ?? 0);

  return (
    <>
      <PageSection>
        <Spacing>
          <Row centered justify="space-between">
            <Heading variant="h3" noMargin centered>
              Fee overview
            </Heading>
            <ButtonGroup>
              <Button
                variant="contained"
                color="primary"
                onClick={handleExport}
                disabled={exportFeesMutation.isPending}
              >
                Export
              </Button>
            </ButtonGroup>
          </Row>
          <StyledRow centered>
            <StyledCard>
              <StyledCardContent>
                <Typography variant="subtitleLarge" gutterBottom>
                  Fees approved to date
                </Typography>
                <Typography variant="titleLarge">
                  {!isLoadingApprovedFees ? (
                    numberToCurrencyString(calculateTotalApprovedFees)
                  ) : (
                    <CircularProgress />
                  )}
                </Typography>
              </StyledCardContent>
            </StyledCard>
            <StyledCard>
              <StyledCardContent>
                <Typography variant="subtitleLarge" gutterBottom>
                  Due fees awaiting approval
                </Typography>
                <Typography variant="titleLarge">
                  {!isLoadingFutureFees && !isLoadingDueFees ? (
                    numberToCurrencyString(calculateTotalUnapprovedFees)
                  ) : (
                    <CircularProgress />
                  )}
                </Typography>
              </StyledCardContent>
            </StyledCard>
          </StyledRow>
          <Row centered justify="space-between">
            <Heading variant="h3" noMargin centered>
              Approved fees
            </Heading>
            <Button onClick={() => setShowApprovedFees(!showApprovedFees)}>
              {showApprovedFees ? 'Hide approved fees' : 'Show approved fees'}
            </Button>
          </Row>
          {showApprovedFees && (
            <>
              {approvedFees && !isLoadingApprovedFees && (
                <Spacing size="sm">
                  <FeesAccordion
                    fees={approvedFees}
                    currentPage={approvedFeesPage}
                    onPageChange={setApprovedFeesPage}
                    feeType={ListLpFeesCategory.ApprovedFees}
                  />
                </Spacing>
              )}
              {isLoadingApprovedFees && <CircularProgress />}
            </>
          )}
        </Spacing>
      </PageSection>

      <PageSection>
        <Spacing>
          <Heading variant="h3" noMargin>
            Unapproved fees due
          </Heading>
          {dueUnapprovedFees && !isLoadingDueFees && (
            <Spacing size="sm">
              <FeesAccordion
                fees={dueUnapprovedFees}
                currentPage={dueFeesPage}
                onPageChange={setDueFeesPage}
                feeType={ListLpFeesCategory.DueUnapprovedFees}
                setApprovalSummaryData={setApprovalSummaryData}
              />
            </Spacing>
          )}
          {isLoadingDueFees && <CircularProgress />}
        </Spacing>
      </PageSection>

      <PageSection>
        <Spacing>
          <Heading variant="h3" noMargin>
            Unapproved future fees
          </Heading>
          {futureUnapprovedFees && !isLoadingFutureFees && (
            <Spacing size="sm">
              <FeesAccordion
                fees={futureUnapprovedFees}
                currentPage={futureFeesPage}
                onPageChange={setFutureFeesPage}
                feeType={ListLpFeesCategory.FutureUnapprovedFees}
                setApprovalSummaryData={setApprovalSummaryData}
              />
            </Spacing>
          )}
          {isLoadingFutureFees && <CircularProgress />}
        </Spacing>
      </PageSection>
    </>
  );
};

export default FeeManager;
